/**
 * 이상한 방 - 핀(pins) 산식
 *
 * 근거 문서: 로드맵 확정안 3.1절(재화 - 핀) · 5절(어휘: 핀/pins, 코인·골드·젬·포인트·도장은 쓰지 않는다)
 *           `이상한방-컨셉시트-v0.1.md` 4.5절(점수) · `이상한방-상태모델-v0.1.md` 11.3절(서버가 쥐는 것)
 *
 * ## 핀은 라운드 결과에서만 생긴다
 *
 * 획득처는 하나(라운드 결과), 산식은 이 파일 하나다. 결제도 광고도 없다. 나중에 결제가 붙어도
 * 파는 것은 핀뿐이고 봉투 규칙은 그대로다(3.1절). 그래서 이 파일에는 "핀을 더하는" 경로가
 * `pinsForOutcome`(싱글)·`pinsForPartyOutcome`(여럿이)·`pinsForDaily`(매일의 방)·`addPins`(환급) 넷뿐이고,
 * 전부 순수 함수다.
 *
 * ## 시계가 없다 (M9에서도 그대로다)
 *
 * 매일의 방 완주·연속 출석(3.1절 표의 마지막 두 줄)은 날짜를 알아야 하는데 이 파일은 시계를 읽지 않는다.
 * M9이 붙인 `pinsForDaily`도 날짜를 받지 않는다 - **며칠째인가만** 받는다(`streakDays`). "어제였는가"의 판정은
 * `game/daily.ts`의 것이고, 그 판정이 여기에도 있으면 두 곳이 갈리는 날 한쪽이 조용히 관대해진다.
 *
 * ## 음수가 되지 않는다
 *
 * 핀은 쓸 때만 줄고(`spendPins`), 부족하면 **상태 참조 동일 + 거부 사유**다(로드맵 고정 규칙 8).
 * 거부 사유에 "몇 판이면 다시 열리는가"가 들어가는 이유는 GLOSSARY 3절("왜 안 되는지는 반드시 말한다") -
 * 눌리지 않는 [한 장 더]는 고장 난 버튼과 구별되지 않는다.
 */

/* --- 상수 ------------------------------------------------------------------- */

/**
 * 보유 상한. 3.1절. 네 자리를 넘지 않는 것은 칩이 잘리지 않는 폭이기도 하다.
 * 상한에 걸린 획득은 조용히 잘린다 - 봉투 30장 값을 쌓아 둔 사람에게 "더 못 준다"는 손해가 아니다.
 */
export const PIN_CAP = 9999

/**
 * 3.1절의 표 그대로. **전부 플레이테스트 대상이다.**
 *
 * 값의 근거는 첫 봉투의 시점 하나다 - 1단계(이상한 곳 3) 23핀 + 2단계(4) 24핀 = 47핀 ≥ 30핀이라
 * 첫 세션 3분 안에 봉투가 열린다. 이후 단계당 20~30핀이라 거의 매 판 한 장이다.
 *
 * `partyFinish`가 승패와 무관한 이유: 지는 판이 0핀이면 "한 판 더"가 죽는다.
 * `dailyFinish`·`streakDay`는 M9에서 `pinsForDaily`가 소비하기 시작했다.
 */
export const PIN_RATES = {
  /** 이상한 곳 1개 */
  found: 1,
  /** 단계 클리어 */
  clear: 5,
  /** 그 단계의 첫 클리어. `clear`에 **더해서** 준다(1단계 3 + 5 + 15 = 23). */
  firstClear: 15,
  /** 퍼펙트(무오답 전량 발견) */
  perfect: 10,
  /** 여럿이 판 완주(승패 무관) */
  partyFinish: 3,
  /** 여럿이 판 1위 */
  partyFirst: 8,
  /** 여럿이 판 2위 */
  partySecond: 5,
  /** 다녀간 사람으로서 끝까지 안 들킨 이상한 곳 1개 */
  visitorUnfound: 2,
  /** 관전 점찍기 적중 1개 */
  callHit: 2,
  /** 매일의 방 완주. M9. */
  dailyFinish: 10,
  /** 연속 출석 하루. `min(n, 7)`. M9. */
  streakDay: 1,
} as const

export type PinReason = keyof typeof PIN_RATES

/**
 * 연속 출석 핀의 상한 일수(`+min(n, 7)`). **연속 일수 자체의 상한이기도 하다** -
 * `game/daily.STREAK_CAP`이 이 수를 그대로 가리키므로 저장된 값이 7에서 멈추고, 화면과 핀이 다른 수를 말할 길이 없다.
 */
export const STREAK_DAYS_CAP = 7

/**
 * "N판이면 다시 연다"를 셀 때 한 판에서 기대하는 핀. 3.1절의 "단계당 20~30핀"의 **아래쪽**을 잡았다 -
 * 넉넉히 말해 놓고 한 판 일찍 열리는 쪽이, 빠듯하게 말해 놓고 약속을 어기는 쪽보다 낫다.
 */
export const TYPICAL_ROUND_PINS = 20

/* --- 타입 ------------------------------------------------------------------- */

/** 산식의 한 줄. `count × PIN_RATES[reason] = pins`. 결과 화면이 한 줄씩 굴려 올린다(로드맵 4절 2번). */
export type PinLine = Readonly<{ reason: PinReason; count: number; pins: number }>

export type PinBreakdown = Readonly<{ lines: readonly PinLine[]; total: number }>

/**
 * 싱글 한 판의 결과 중 핀이 보는 것. `RoundOutcome`(`sessionResult.ts`)에서 뽑아 넘긴다 -
 * 그 타입을 직접 받지 않는 이유는 그쪽이 접착층의 것이고 이 파일은 서버로 갈 코드라서다.
 */
export type SoloOutcome = Readonly<{
  foundCount: number
  cleared: boolean
  /** 무오답 전량 발견. `hunt.computeScoreBreakdown().isPerfect`와 같은 값이어야 한다. */
  perfect: boolean
}>

/** 싱글 산식이 진행에서 알아야 하는 것. 첫 클리어 여부는 저장(`progress`)이 안다. */
export type SoloContext = Readonly<{ firstClear: boolean }>

/**
 * 매일의 방 한 판의 결과 중 핀이 보는 것. 날짜가 아니라 **며칠째인가**만 받는다(머리말).
 * `game/daily.finishDaily`가 그 둘을 함께 답한다 - `first`가 곧 `finished`이고 `streak`이 `streakDays`다.
 */
export type DailyPinOutcome = Readonly<{
  /** 그날의 **첫** 완주였는가. 재도전(연습)은 거짓이라 핀이 0이다(로드맵 M9 규칙 2 - 첫 결과만). */
  finished: boolean
  /** 연속 출석 일수. `STREAK_DAYS_CAP`에서 잘린다. */
  streakDays: number
}>

/** 여럿이 한 판의 결과 중 핀이 보는 것. 호스트가 `roundRewards`로 계산해 보낸다(로드맵 2.2절). */
export type PartyOutcome = Readonly<{
  /** 끝까지 있었는가. 중간에 나간 사람은 완주 핀이 없다. */
  finished: boolean
  /** 1부터. 다녀간 사람은 순위가 없다(`null`). */
  rank: number | null
  /** 다녀간 사람으로서 안 들킨 이상한 곳 수. 찾는 사람이면 0. */
  unfoundAsVisitor: number
  /** 관전 점찍기 적중 수. */
  callHits: number
}>

/* --- 순수 헬퍼 ------------------------------------------------------------ */

/** 개수로 읽는다. NaN·음수·소수는 0 또는 내림이다 - 핀 산식에 오염된 수가 들어와도 음수가 나오지 않는다. */
function toCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  return Math.floor(value)
}

function line(reason: PinReason, count: number): PinLine | null {
  const n = toCount(count)
  if (n === 0) return null
  return { reason, count: n, pins: n * PIN_RATES[reason] }
}

function breakdown(lines: readonly (PinLine | null)[]): PinBreakdown {
  const kept = lines.filter((l): l is PinLine => l !== null)
  return { lines: kept, total: kept.reduce((sum, l) => sum + l.pins, 0) }
}

/* --- 산식 -------------------------------------------------------------------- */

/**
 * 싱글 한 판의 핀. 3.1절 표의 위 네 줄이다.
 *
 * 줄 순서가 결과 화면의 굴러 오르기 순서다 - 작은 것부터 쌓여 마지막이 가장 크게(리빌의 원칙과 같다).
 * 첫 클리어는 클리어에 더해진다. 못 깬 판은 발견 핀만 받고, 그것도 0이면 빈 산식이다(0핀은 사실이다).
 */
export function pinsForOutcome(outcome: SoloOutcome, context: SoloContext): PinBreakdown {
  return breakdown([
    line('found', outcome.foundCount),
    line('clear', outcome.cleared ? 1 : 0),
    line('firstClear', outcome.cleared && context.firstClear ? 1 : 0),
    line('perfect', outcome.perfect ? 1 : 0),
  ])
}

/**
 * 여럿이 한 판의 핀. 3.1절 표의 가운데 네 줄이다. 호스트(리듀서 + `net/matchHost.ts`)가 부른다.
 *
 * 순위 핀은 완주한 사람에게만 - 완주 없이 순위가 있을 수 없다. 3위 이하는 완주 핀만 받는다.
 */
export function pinsForPartyOutcome(outcome: PartyOutcome): PinBreakdown {
  const finished = outcome.finished
  return breakdown([
    line('partyFinish', finished ? 1 : 0),
    line('partyFirst', finished && outcome.rank === 1 ? 1 : 0),
    line('partySecond', finished && outcome.rank === 2 ? 1 : 0),
    line('visitorUnfound', outcome.unfoundAsVisitor),
    line('callHit', outcome.callHits),
  ])
}

/**
 * 매일의 방 한 판의 핀. 3.1절 표의 **마지막 두 줄**이다.
 *
 * 완주하지 않았으면(그날 것을 이미 했다 · 지나간 날) 빈 산식이다 - 출석 핀만 따로 붙는 경로를 두지 않는 것이
 * 규칙이다. 그 문을 열면 "매일의 방을 열기만 하고 나가기"가 최적이 되고, 그러면 매일의 방이 아니라 매일의 버튼이 된다.
 * 연속 핀은 상한에서 잘린다(`STREAK_DAYS_CAP`) - 7일째와 30일째가 같은 값인 것이 3.1절의 `min(n, 7)`이다.
 */
export function pinsForDaily(outcome: DailyPinOutcome): PinBreakdown {
  if (!outcome.finished) return breakdown([])
  return breakdown([
    line('dailyFinish', 1),
    line('streakDay', Math.min(toCount(outcome.streakDays), STREAK_DAYS_CAP)),
  ])
}

/* --- 보유량 ------------------------------------------------------------------ */

/**
 * 저장값·인자로 들어온 수를 보유량으로 정규화한다. 유한한 0 이상의 정수만 살고, 상한에서 잘린다.
 * 한 번 저장된 `NaN`은 `JSON.stringify`에서 `null`이 되고 그 뒤로 원인을 못 찾는다(`progress.ts`와 같은 이유).
 */
export function normalizePins(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0
  return Math.min(PIN_CAP, Math.floor(value))
}

/**
 * 핀을 더한다. 음수 델타는 **거부한다**(0을 더한 것과 같다) - 핀이 줄어드는 경로는 `spendPins` 하나여야
 * 어디서 줄었는지 추적할 수 있다. 상한을 넘는 몫은 조용히 잘린다.
 */
export function addPins(pins: number, delta: number): number {
  return normalizePins(normalizePins(pins) + toCount(delta))
}

export type SpendResult =
  | Readonly<{ ok: true; pins: number }>
  | Readonly<{
      ok: false
      /** 입력과 **같은 값**. 거부되면 보유량은 한 핀도 움직이지 않는다. */
      pins: number
      /** 얼마가 모자라는가. */
      shortBy: number
      /** "N판이면 다시 연다"의 N. 최소 1. */
      roundsToAfford: number
    }>

/** 얼마나 모자라는지를 판 수로 바꾼다. 0이 아닌 부족분은 언제나 1판 이상이다. */
export function roundsToAfford(shortBy: number): number {
  const short = toCount(shortBy)
  if (short === 0) return 0
  return Math.max(1, Math.ceil(short / TYPICAL_ROUND_PINS))
}

/**
 * "봉투까지 몇 핀"의 한 자리. `ok`면 0이고, 아니면 부족분이다. 화면(결과 화면의 게이지 · 선택 화면의 계정 줄)은
 * 이 수를 받아 그리기만 한다 - `price - pins`를 화면마다 적으면 부족분 산식이 셋이 된다.
 */
export function shortByOf(result: SpendResult): number {
  return result.ok ? 0 : result.shortBy
}

/**
 * 핀을 쓴다(봉투 값). 부족하면 `ok: false`이고 `pins`는 입력 그대로다.
 *
 * `cost`가 양의 정수가 아니면 호출자의 버그라 던진다 - 봉투 값은 표(`envelopeTable.ts`)에서만 오고,
 * 그 값이 이상하면 조용히 0을 쓰는 것보다 첫 테스트에서 걸리는 쪽이 낫다.
 *
 * @throws {RangeError} `cost`가 양의 정수가 아닐 때
 */
export function spendPins(pins: number, cost: number): SpendResult {
  if (!Number.isSafeInteger(cost) || cost <= 0) {
    throw new RangeError(`spendPins: 값은 양의 정수여야 한다 (받은 값: ${String(cost)})`)
  }
  const held = normalizePins(pins)
  if (held < cost) {
    const shortBy = cost - held
    return { ok: false, pins, shortBy, roundsToAfford: roundsToAfford(shortBy) }
  }
  return { ok: true, pins: held - cost }
}
